import { useEffect, useRef } from 'react';
import { useBadges, type UnlockedBadge } from '@/hooks/useBadges';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const STORAGE_KEY = 'focustrack_unlocked_badges';
const INIT_FLAG_KEY = 'focustrack_badges_initialized';

const ACHIEVEMENT_COLORS = ['#FFD700', '#FFA500', '#10B981', '#A78BFA', '#F472B6', '#FBBF24'];

function fireAchievementConfetti() {
  const baseOptions = {
    particleCount: 80,
    spread: 70,
    startVelocity: 45,
    gravity: 1.1,
    ticks: 200,
    colors: ACHIEVEMENT_COLORS,
    zIndex: 9999,
  };
  // Two cannons from top-left and top-right toward center
  confetti({ ...baseOptions, origin: { x: 0.1, y: 0.05 }, angle: -60 });
  confetti({ ...baseOptions, origin: { x: 0.9, y: 0.05 }, angle: -120 });
  // Center burst slightly delayed
  setTimeout(() => {
    confetti({
      ...baseOptions,
      particleCount: 120,
      spread: 100,
      origin: { x: 0.5, y: 0.1 },
      angle: -90,
    });
  }, 200);
}

function showAchievementToast(badge: UnlockedBadge) {
  toast.custom(
    (id) => (
      <div
        onClick={() => toast.dismiss(id)}
        className="flex items-center gap-3 rounded-xl border-2 border-yellow-500/60 bg-gradient-to-br from-yellow-500/20 via-orange-500/20 to-amber-500/20 backdrop-blur-md px-4 py-3 shadow-2xl shadow-yellow-500/20 cursor-pointer min-w-[300px] max-w-[360px] animate-in slide-in-from-top-2 fade-in duration-300"
        style={{
          boxShadow: '0 10px 40px -10px rgba(234, 179, 8, 0.4), 0 0 0 1px rgba(234, 179, 8, 0.1)',
        }}
      >
        <div className="flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-yellow-400/30 to-orange-500/30 ring-2 ring-yellow-400/50 shrink-0">
          <span className="text-3xl drop-shadow-lg">{badge.icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-yellow-700 dark:text-yellow-400">
            <span>🏆</span>
            <span>Nova Conquista!</span>
          </div>
          <p className="font-semibold text-sm text-foreground mt-0.5 truncate">{badge.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{badge.description}</p>
        </div>
      </div>
    ),
    {
      duration: 8000,
      position: 'top-right',
    },
  );
}

export function AchievementWatcher() {
  const { badges } = useBadges();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (badges.length === 0) return;

    const unlockedIds = new Set(badges.filter((b) => b.unlocked).map((b) => b.id));

    // First load: seed storage without firing notifications
    if (!initializedRef.current) {
      initializedRef.current = true;
      const initialized = localStorage.getItem(INIT_FLAG_KEY) === '1';
      if (!initialized) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...unlockedIds]));
        localStorage.setItem(INIT_FLAG_KEY, '1');
        return;
      }
    }

    // Read previously known unlocked ids
    let known: Set<string>;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      known = new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      known = new Set<string>();
    }

    // Find newly unlocked badges
    const newlyUnlocked = badges.filter((b) => b.unlocked && !known.has(b.id));
    if (newlyUnlocked.length === 0) return;

    // Persist updated set immediately to avoid duplicate fires
    const merged = new Set([...known, ...newlyUnlocked.map((b) => b.id)]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...merged]));

    // Fire confetti once per batch
    fireAchievementConfetti();

    // Stagger toasts so multiple unlocks are readable
    newlyUnlocked.forEach((badge, i) => {
      setTimeout(() => showAchievementToast(badge), i * 600);
    });
  }, [badges]);

  return null;
}
